import type { Contact, MinorEligibility } from '@/types/person/contacts';

import { contactsService } from '~/services/contacts-service';

export const useContactsStore = defineStore('contacts', () => {
    const personStore = usePersonStore();
    const { showSuccess, showError } = useGlobalToast();
    const selectedContact = ref<Contact | null>(null);

    //state
    const contacts = ref<Contact[]>([]);
    const selfMember = ref<Contact | null>(null);
    const searchQuery = ref('');
    const minorConsentVariant = ref<'standard' | 'bekiares'>('standard');

    const tags = ref([
        { name: 'Friends', code: 'friend' },
        { name: 'Gamers', code: 'gamer' },
        { name: 'Family', code: 'family' },
        { name: 'Coworkers', code: 'coworker' },
    ]);

    // Computed getter for filtered contacts
    const filteredContacts = computed(() => {
        let filtered = contacts.value;

        // Apply search filter
        if (searchQuery.value) {
            const lowerCaseTerm = searchQuery.value.toLowerCase();
            filtered = filtered.filter((contact) =>
                contact.name.toLowerCase().includes(lowerCaseTerm)
            );
        }

        return filtered;
    });

    async function fetchContacts(includeSelf: boolean = true) {
        if (!personStore.person) return;

        try {
            const response = await contactsService.getContacts(personStore.person.id, includeSelf);
            contacts.value = response.data;
            selfMember.value = response.self || null;
        } catch (error: any) {
            showError("Error", error.message || 'Failed to fetch contacts');
        }
    }

    async function createContact(contactData: any) {
        if (!personStore.person) {
            showError("Error", 'User not found');
            return false;
        }

        try {
            await contactsService.createContact(personStore.person.id, contactData);
            await fetchContacts(); // Refresh the contacts list
            showSuccess("Success", 'Contact created successfully');
            return true;
        } catch (error: any) {
            showError("Error", error.message || 'Failed to create contact');
            return false;
        }
    }

    const removeContact = async (contact_id: number) => {
        if (!personStore.person) return;

        try {
            await contactsService.deleteContact(personStore.person.id, contact_id);
            
            // Only remove the contact from the contacts after successful API call
            contacts.value = contacts.value.filter(contact => contact.id !== contact_id);
            await fetchContacts(); // Refresh the contacts list
            showSuccess("Success", 'Contact removed successfully');
            selectedContact.value = null;
        } catch (error: any) {
            showError("Error", error.message || 'Something went wrong while removing contact');
            // Don't update local state if the API call failed
        }
    };

    const searchContacts = (query: string) => {
        searchQuery.value = query;
    };

    const clearSearch = () => {
        searchQuery.value = '';
    };

    async function checkMinorEligibility(): Promise<MinorEligibility> {
        if (!personStore.person) {
            return { eligible: false, reason: 'User not found' };
        }

        try {
            const result = await contactsService.checkMinorEligibility(personStore.person.id);
            if (result?.agreement_variant) {
                minorConsentVariant.value = result.agreement_variant;
            }
            return result;
        } catch (error: any) {
            showError("Error", error.message || 'Failed to check eligibility');
            return { eligible: false, reason: error.message };
        }
    }

    async function signMinorConsent(signerLegalName: string) {
        if (!personStore.person) {
            showError("Error", 'User not found');
            return false;
        }

        try {
            await contactsService.signMinorConsent(personStore.person.id, signerLegalName);
            personStore.person.minor_consent_signed = true;
            personStore.person.minor_consent_signed_at = new Date().toISOString();
            return true;
        } catch (error: any) {
            showError("Error", error.message || 'Failed to sign consent');
            return false;
        }
    }

    async function createMinorChild(childData: {
        first_name: string;
        last_name: string;
        dob: string;
    }) {
        if (!personStore.person) {
            showError("Error", 'User not found');
            return false;
        }

        try {
            await contactsService.createMinorChild(personStore.person.id, childData);
            await fetchContacts();
            showSuccess("Success", 'Child added to your family protection plan');
            return true;
        } catch (error: any) {
            showError("Error", error.message || 'Failed to add child');
            return false;
        }
    }

    return {
        contacts,
        selfMember,
        selectedContact,
        fetchContacts,
        createContact,
        tags,
        removeContact,
        searchContacts,
        clearSearch,
        searchQuery,
        filteredContacts,
        checkMinorEligibility,
        signMinorConsent,
        createMinorChild,
        minorConsentVariant,
    };
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useContactsStore, import.meta.hot))
}